/**
 * Settings Service
 * Handles user_settings management and preference storage
 */

import { UserSettingsRecord } from '@/types/collections';
import { createRepository } from '@/services/repository';

export type Theme = 'light' | 'dark' | 'auto';
export type Language = 'de' | 'en' | 'nl' | 'es' | 'fr';

class SettingsService {
  private settingsRepo = createRepository<UserSettingsRecord>('user_settings');

  setUserId(userId: string): void {
    this.settingsRepo.setUserId(userId);
  }

  /**
   * Get user settings (or create defaults if not found)
   */
  async getSettings() {
    try {
      const result = await this.settingsRepo.list();
      if (result.records.length > 0) {
        return result.records[0]; // Return first/only settings record
      }

      // Create default settings
      return this.settingsRepo.create({
        theme: 'auto',
        language: 'de',
        notifications: {
          email: true,
          push: true,
          reminders: true,
        },
        offlineMode: true,
        autoSync: true,
      });
    } catch (error) {
      console.error('[Settings] getSettings error:', error);
      throw error;
    }
  }

  /**
   * Update theme preference
   */
  async updateTheme(theme: Theme) {
    try {
      const settings = await this.getSettings();
      return this.settingsRepo.update(settings.id, { theme });
    } catch (error) {
      console.error('[Settings] updateTheme error:', error);
      throw error;
    }
  }

  /**
   * Update language preference
   */
  async updateLanguage(language: Language) {
    try {
      const settings = await this.getSettings();
      return this.settingsRepo.update(settings.id, { language });
    } catch (error) {
      console.error('[Settings] updateLanguage error:', error);
      throw error;
    }
  }

  /**
   * Update notification settings
   */
  async updateNotifications(notifications: {
    email?: boolean;
    push?: boolean;
    reminders?: boolean;
  }) {
    try {
      const settings = await this.getSettings();
      return this.settingsRepo.update(settings.id, { notifications });
    } catch (error) {
      console.error('[Settings] updateNotifications error:', error);
      throw error;
    }
  }

  /**
   * Toggle offline mode
   */
  async toggleOfflineMode(enabled: boolean) {
    try {
      const settings = await this.getSettings();
      return this.settingsRepo.update(settings.id, { offlineMode: enabled });
    } catch (error) {
      console.error('[Settings] toggleOfflineMode error:', error);
      throw error;
    }
  }

  /**
   * Toggle auto-sync
   */
  async toggleAutoSync(enabled: boolean) {
    try {
      const settings = await this.getSettings();
      return this.settingsRepo.update(settings.id, { autoSync: enabled });
    } catch (error) {
      console.error('[Settings] toggleAutoSync error:', error);
      throw error;
    }
  }
}

export const settingsService = new SettingsService();

